import { Component, Input, Output, EventEmitter } from '@angular/core';
import { CommonModule } from '@angular/common';
import { SubRouteNavigationComponent } from '../sub-route-navigation/sub-route-navigation.component';
import { BusinessPartnerNavigationComponent } from '../business-partner-navigation/business-partner-navigation.component';
import { RouteNavigationEvent } from '../../services/sub-route-navigation.service';
import { BusinessPartnerNavigationEvent } from '../business-partner-navigation/interfaces/business-partner-navigation.interfaces';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';

export interface UserData {
  _id?: string;
  username?: string;
  name?: string;
}

export interface ResponsibleData {
  _id?: string;
  name?: string;
  personNumber?: string;
  externalKeys?: {
    code?: string;
  };
}

export interface RouteData {
  _id?: string;
  routeName?: string;
  routeCode?: string;
  externalKeys?: {
    code?: string;
  };
  model?: string;
}

@Component({
  selector: 'app-user-route-display',
  standalone: true,
  imports: [
    CommonModule,
    MatIconModule,
    MatTooltipModule,
    SubRouteNavigationComponent,
    BusinessPartnerNavigationComponent
  ],
  templateUrl: './user-route-display.component.html',
  styleUrls: ['./user-route-display.component.scss']
})
export class UserRouteDisplayComponent {
  @Input() userData: UserData | string | null = null;
  @Input() routeData: RouteData | string | null = null;
  @Input() responsibleData: ResponsibleData | null = null;
  @Input() displayMode: 'combined' | 'route-first' | 'user-first' | 'detailed' = 'route-first';
  @Input() showRoute: boolean = true;
  @Input() showUser: boolean = true;
  @Input() showResponsible: boolean = true;
  @Input() separator: string = ' - ';
  @Input() showRouteCode: boolean = false;

  @Input() showUserIcon: boolean = false; // si mostrar icono
  @Input() userIconDefault: string = 'location_on'; // icono por defecto
  @Input() userIconHover: string = 'person_pin_circle'; // icono al hacer hover
  @Input() userIconTooltip: string | null = null;

  // Navigation inputs (enabled by default)
  @Input() enableRouteNavigation: boolean = true;
  @Input() enableResponsibleNavigation: boolean = true;
  @Input() openNavigationInNewTab: boolean = true;
  @Input() navigationInteractionMode: 'both' | 'text-only' | 'icon-only' = 'icon-only';
  isUserIconHovered: boolean = false;

  // Navigation outputs
  @Output() onRouteNavigate = new EventEmitter<RouteNavigationEvent>();
  @Output() onResponsibleNavigate = new EventEmitter<BusinessPartnerNavigationEvent>();
  @Output() onUserIconClickEvent = new EventEmitter<void>();

  get displayText(): string {
    const routeValue = this.getRouteValue();
    const userValue = this.getUserValue();

    if (!this.showRoute && !this.showUser) {
      return '';
    }

    if (!this.showRoute) {
      return userValue || '-';
    }

    if (!this.showUser) {
      return routeValue || '-';
    }

    const route = routeValue || '';
    const user = userValue || '';

    if (!route && !user) {
      return '-';
    }

    switch (this.displayMode) {
      case 'route-first':
        if (!route) return user;
        if (!user) return route;
        return `${route}${this.separator}${user}`;
      
      case 'user-first':
        if (!user) return route;
        if (!route) return user;
        return `${user}${this.separator}${route}`;
      
      case 'combined':
        if (!route) return user;
        if (!user) return route;
        return `${route} (${user})`;
      
      default:
        return `${route}${this.separator}${user}`;
    }
  }

  get routeCodeValue(): string {
    if (!this.showRouteCode || !this.routeData || typeof this.routeData === 'string') {
      return '';
    }
    return this.routeData.externalKeys?.code || this.routeData.routeCode || this.routeData.model || '';
  }

  get routeNameValue(): string {
    if (!this.routeData) return '';
    
    if (typeof this.routeData === 'string') {
      return this.routeData;
    }
    
    return this.routeData.routeName || '';
  }

  get userValue(): string {
    if (!this.userData) return '';
    
    if (typeof this.userData === 'string') {
      return this.userData;
    }
    
    return this.userData.username || this.userData.name || '';
  }

  get responsibleValue(): string {
    if (!this.responsibleData) return '';
    return this.responsibleData.name || '';
  }

  get responsibleCodeValue(): string {
    if (!this.responsibleData) return '';
    return this.responsibleData.externalKeys?.code || '';
  }

  /**
   * Check if route is navigable (has navigation enabled and valid data)
   */
  get isRouteNavigable(): boolean {
    if (!this.enableRouteNavigation || !this.routeData || typeof this.routeData === 'string') {
      return false;
    }

    return !!(
      this.routeData._id ||
      this.routeData.routeCode ||
      this.routeData.externalKeys?.code ||
      this.routeData.routeName
    );
  }

  /**
   * Check if responsible (business partner) is navigable
   */
  get isResponsibleNavigable(): boolean {
    if (!this.enableResponsibleNavigation || !this.responsibleData) {
      return false;
    }

    return !!(
      this.responsibleData._id ||
      this.responsibleData.externalKeys?.code ||
      this.responsibleData.name
    );
  }

  /**
   * Get route data formatted for navigation component
   */
  get routeNavigationData(): any {
    if (!this.routeData || typeof this.routeData === 'string') {
      return null;
    }
    return this.routeData;
  }

  /**
   * Get responsible data formatted for navigation component
   */
  get responsibleNavigationData(): any {
    return this.responsibleData;
  }

  /**
   * Handle route navigation event
   */
  handleRouteNavigation(event: RouteNavigationEvent): void {
    this.onRouteNavigate.emit(event);
  }

  /**
   * Handle responsible navigation event
   */
  handleResponsibleNavigation(event: BusinessPartnerNavigationEvent): void {
    this.onResponsibleNavigate.emit(event);
  }

  private getRouteValue(): string {
    return this.routeNameValue;
  }

  private getUserValue(): string {
    return this.userValue;
  }

  handleUserIconClick(): void {
    this.onUserIconClickEvent.emit();
  }
}